import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, MapPin, Users, ArrowRight, Sparkles, MessageCircle, Ticket, UserPlus, Timer, Star, Trophy } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import Header from "../components/Header";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { getBadge } from "../lib/badges";
import { isNativeApp, openExternal } from "../lib/native";

const HERO_IMG = "/img/hero.jpg";

const PLACEHOLDER = "/img/event-placeholder.jpg";

function formatDate(iso, lang) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(lang === "ar" ? "ar" : "en", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function timeLeft(f, t) {
  const target = f.expires_at || (f.event_date ? `${f.event_date}T23:59:59` : null);
  if (!target) return null;
  const ms = new Date(target).getTime() - Date.now();
  if (ms <= 0) return null;
  const days = Math.floor(ms / 86400000);
  const hours = Math.floor((ms % 86400000) / 3600000);
  const mins = Math.floor((ms % 3600000) / 60000);
  let label;
  if (days > 0) label = `${days} ${t("daysUnit")}${hours > 0 ? ` و ${hours} ${t("hoursUnit")}` : ""}`;
  else if (hours > 0) label = `${hours} ${t("hoursUnit")}${mins > 0 ? ` و ${mins} ${t("minsUnit")}` : ""}`;
  else label = `${mins} ${t("minsUnit")}`;
  return { label, urgent: ms < 86400000 };
}

export default function Landing() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const loggedIn = user && user !== false;
  const [forms, setForms] = useState(null);
  const [highlights, setHighlights] = useState(null);
  const [, setTick] = useState(0);

  useEffect(() => {
    api
      .get("/forms/public")
      .then((r) => setForms(Array.isArray(r.data) ? r.data : []))
      .catch(() => setForms([]));
    api.get("/public/highlights").then((r) => setHighlights(r.data)).catch(() => {});
    const iv = setInterval(() => setTick((x) => x + 1), 60000);
    return () => clearInterval(iv);
  }, []);

  const pickTitle = (f) => (lang === "ar" ? f.title_ar || f.title : f.title);
  const pickDesc = (f) => (lang === "ar" ? f.description_ar || f.description : f.description);
  const upcoming = (forms || [])
    .filter((f) => f.event_date && f.event_date >= new Date().toISOString().slice(0, 10))
    .sort((a, b) => (a.event_date > b.event_date ? 1 : -1));

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Header />

      {/* UPCOMING STRIP — top of page */}
      {upcoming.length > 0 && (
        <section data-testid="upcoming-strip" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
          <div className="rounded-3xl bg-slate-900 dark:bg-slate-800 text-white p-5 sm:p-7 overflow-hidden relative">
            <div className="absolute -top-20 -end-20 w-64 h-64 rounded-full bg-blue-600/30 blur-3xl" />
            <div className="absolute -bottom-24 -start-16 w-56 h-56 rounded-full bg-red-600/20 blur-3xl" />
            <div className="flex items-center justify-between mb-4 relative">
              <p className="text-xs tracking-[0.25em] uppercase text-blue-300 flex items-center gap-2">
                <CalendarDays className="w-4 h-4" />
                {t("upcomingEvents")}
              </p>
              <span className="text-[11px] text-white/50">{upcoming.length}</span>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-1 relative" style={{ scrollbarWidth: "thin" }}>
              {upcoming.map((f) => {
                const left = timeLeft(f, t);
                return (
                  <Link
                    key={f.id}
                    to={`/f/${f.id}`}
                    data-testid={`upcoming-card-${f.id}`}
                    className="shrink-0 rounded-2xl bg-white/10 hover:bg-white/20 border border-white/10 p-4 transition-colors min-w-[250px] max-w-[290px]"
                  >
                    <p className="font-display font-semibold truncate">{pickTitle(f)}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-blue-200">
                      <span className="inline-flex items-center gap-1">
                        <CalendarDays className="w-3.5 h-3.5" />
                        {formatDate(f.event_date, lang)}
                      </span>
                      {f.location && (
                        <span className="inline-flex items-center gap-1 truncate max-w-[140px]">
                          <MapPin className="w-3.5 h-3.5" />
                          {f.location}
                        </span>
                      )}
                    </div>
                    {left && (
                      <span
                        className={`mt-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold ${
                          left.urgent ? "bg-red-500/25 text-red-200 animate-pulse" : "bg-amber-400/20 text-amber-200"
                        }`}
                      >
                        <Timer className="w-3.5 h-3.5" />
                        {t("regTimeLeft")}: {left.label}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 lg:pt-20 pb-12 grid lg:grid-cols-5 gap-8 lg:gap-16 items-center">
          <div className="lg:col-span-3 fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-xs font-medium tracking-[0.15em] uppercase mb-6">
              <Sparkles className="w-3.5 h-3.5" />
              {t("theme")}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.05]">
              {t("heroHeadline")}
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-600 max-w-2xl leading-relaxed">
              {t("heroSub")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#events">
                <Button
                  data-testid="hero-explore-btn"
                  size="lg"
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8"
                >
                  {t("exploreCta")}
                  <ArrowRight className="w-4 h-4 ms-2 rtl:rotate-180" />
                </Button>
              </a>
              <a
                href="https://wa.me/966555941767"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  if (isNativeApp()) {
                    e.preventDefault();
                    openExternal("https://wa.me/966555941767");
                  }
                }}
              >
                <Button
                  data-testid="hero-whatsapp-btn"
                  variant="outline"
                  size="lg"
                  className="rounded-full border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800 px-8"
                >
                  <MessageCircle className="w-4 h-4 me-2" />
                  {t("whatsappCta")}
                </Button>
              </a>
              <Link to={loggedIn ? "/account" : "/my-tickets"}>
                <Button
                  data-testid="my-tickets-btn"
                  variant="outline"
                  size="lg"
                  className="rounded-full border-blue-300 text-blue-700 hover:bg-blue-50 hover:text-blue-800 px-8"
                >
                  <Ticket className="w-4 h-4 me-2" />
                  {t("myTickets")}
                </Button>
              </Link>
              {!loggedIn && (
                <Link to="/register">
                  <Button
                    data-testid="hero-register-btn"
                    variant="outline"
                    size="lg"
                    className="rounded-full border-blue-300 text-blue-700 hover:bg-blue-50 hover:text-blue-800 px-8"
                  >
                    <UserPlus className="w-4 h-4 me-2" />
                    {t("createAccount")}
                  </Button>
                </Link>
              )}
            </div>
          </div>

          <div className="lg:col-span-2 fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="relative aspect-[4/5] rounded-3xl overflow-hidden shadow-xl border border-slate-200">
              <img
                src={HERO_IMG}
                alt=""
                aria-hidden="true"
                className="w-full h-full object-cover"
                loading="eager"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 via-transparent to-transparent" />
              <div className="absolute bottom-6 start-6 end-6 text-white">
                <p className="text-xs tracking-[0.2em] uppercase opacity-80">{t("brand")}</p>
                <p className="mt-1 font-display text-xl font-semibold">{t("tagline")}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* EVENTS */}
      <section id="events" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-blue-700 mb-2">
              {t("activeEvents")}
            </p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold text-slate-900">
              {t("activeEvents")}
            </h2>
          </div>
        </div>

        {forms === null ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-72 rounded-2xl" />
            ))}
          </div>
        ) : forms.length === 0 ? (
          <div
            data-testid="no-events-placeholder"
            className="border border-dashed border-slate-300 rounded-3xl p-12 text-center bg-white/50"
          >
            <p className="text-slate-500">{t("noEvents")}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {forms.map((f, idx) => {
              const capacityLabel =
                f.max_submissions
                  ? `${f.submissions_count || 0} ${t("of")} ${f.max_submissions}`
                  : `${f.submissions_count || 0}`;
              return (
                <Link
                  to={`/f/${f.id}`}
                  key={f.id}
                  data-testid={`event-card-${f.id}`}
                  className="event-card group block rounded-2xl overflow-hidden bg-white border border-slate-200 fade-up"
                  style={{ animationDelay: `${idx * 0.05}s` }}
                >
                  <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                    <img
                      src={f.cover_image || PLACEHOLDER}
                      alt=""
                      aria-hidden="true"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                      onError={(e) => {
                        if (e.currentTarget.src !== window.location.origin + PLACEHOLDER) {
                          e.currentTarget.src = PLACEHOLDER;
                        } else {
                          e.currentTarget.style.display = "none";
                        }
                      }}
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="font-display text-lg font-semibold text-slate-900 line-clamp-2">
                      {pickTitle(f) || t("unnamedForm")}
                    </h3>
                    {pickDesc(f) && (
                      <p className="mt-2 text-sm text-slate-600 line-clamp-2">{pickDesc(f)}</p>
                    )}

                    <div className="mt-4 flex flex-wrap gap-2 text-xs text-slate-500">
                      {f.event_date && (
                        <span className="inline-flex items-center gap-1">
                          <CalendarDays className="w-3.5 h-3.5" />
                          {formatDate(f.event_date, lang)}
                        </span>
                      )}
                      {f.location && (
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="w-3.5 h-3.5" />
                          {f.location}
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1">
                        <Users className="w-3.5 h-3.5" />
                        {capacityLabel}
                      </span>
                    </div>

                    <div className="mt-5 flex items-center justify-between">
                      <Badge className="bg-red-100 text-red-800 hover:bg-red-100 rounded-full">
                        {t("register")}
                      </Badge>
                      <ArrowRight className="w-4 h-4 text-blue-600 rtl:rotate-180 group-hover:translate-x-1 rtl:group-hover:-translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>

      {highlights && (highlights.comments.length > 0 || highlights.top_participants.length > 0 || (highlights.top_volunteers || []).length > 0) && (
        <section data-testid="highlights-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {highlights.comments.length > 0 && (
              <div data-testid="featured-comments" className="rounded-3xl bg-white border border-slate-200 p-6 sm:p-8">
                <h2 className="font-display text-lg font-semibold text-slate-900 mb-5 flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-blue-600" />
                  {t("highlightsComments")}
                </h2>
                <div className="space-y-4">
                  {highlights.comments.slice(0, 5).map((c, i) => (
                    <div key={i} className="rounded-2xl bg-slate-50 border border-slate-100 p-4">
                      <div className="flex items-center gap-0.5 mb-2" dir="ltr">
                        {[1, 2, 3, 4, 5].map((s) => (
                          <Star key={s} className={`w-3.5 h-3.5 ${s <= c.rating ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />
                        ))}
                      </div>
                      <p className="text-sm text-slate-700 leading-relaxed">“{c.comment}”</p>
                      <p className="mt-2 text-xs text-slate-400">
                        {c.name && <span className="font-semibold text-slate-500">{c.name}</span>}
                        {c.form_title && <span> — {c.form_title}</span>}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {highlights.top_participants.length > 0 && (
              <div data-testid="top-participants" className="rounded-3xl bg-slate-900 text-white p-6 sm:p-8 relative overflow-hidden">
                <div className="absolute -top-16 -end-16 w-48 h-48 rounded-full bg-amber-500/20 blur-3xl" />
                <h2 className="font-display text-lg font-semibold mb-5 flex items-center gap-2 relative">
                  <Trophy className="w-5 h-5 text-amber-400" />
                  {t("topParticipants")}
                </h2>
                <div className="space-y-3 relative">
                  {highlights.top_participants.map((p, i) => {
                    const b = getBadge(p.count);
                    return (
                      <div key={i} className="flex items-center gap-3 rounded-2xl bg-white/5 border border-white/10 p-3.5">
                        <span className="w-9 h-9 rounded-full grid place-items-center text-lg font-bold bg-white/10">
                          {["🥇", "🥈", "🥉", "4", "5"][i]}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm truncate">{p.name}</p>
                          {b && (
                            <span className="text-[10px] text-white/60">
                              {b.emoji} {t(b.labelKey)}
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-amber-300 font-bold whitespace-nowrap">
                          {p.count} ⭐️
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {(highlights.top_volunteers || []).length > 0 && (
              <div data-testid="top-volunteers" className="rounded-3xl bg-gradient-to-br from-amber-600 to-amber-800 text-white p-6 sm:p-8 relative overflow-hidden">
                <div className="absolute -top-16 -start-16 w-48 h-48 rounded-full bg-white/10 blur-3xl" />
                <h2 className="font-display text-lg font-semibold mb-5 flex items-center gap-2 relative">
                  <Trophy className="w-5 h-5 text-amber-200" />
                  {t("topVolunteers")}
                </h2>
                <div className="space-y-3 relative">
                  {highlights.top_volunteers.map((p, i) => (
                    <div key={i} className="flex items-center gap-3 rounded-2xl bg-white/10 border border-white/15 p-3.5">
                      <span className="w-9 h-9 rounded-full grid place-items-center text-lg font-bold bg-white/15">
                        {["🥇", "🥈", "🥉", "4", "5"][i]}
                      </span>
                      <p className="flex-1 min-w-0 font-semibold text-sm truncate">{p.name}</p>
                      <span className="text-xs text-amber-100 font-bold whitespace-nowrap">
                        {p.hours} {t("hoursUnit")} ⏱
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      <footer className="border-t border-slate-200 py-8 bg-white/60">
        <div data-testid="footer-text" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-sm text-slate-500">
          {t("footerText")}
          <div className="mt-3 flex items-center justify-center gap-4 text-xs">
            <Link to="/privacy" data-testid="footer-privacy-link" className="text-blue-600 hover:underline">
              {t("privacyPolicy")}
            </Link>
            <span className="text-slate-300">|</span>
            <Link to="/terms" data-testid="footer-terms-link" className="text-blue-600 hover:underline">
              {t("termsOfUse")}
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
